/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useLazyQuery, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Tooltip } from 'appkit-react';

import {
    showReportMenuVar, leftNavigationItemsVar, moduleIdVar, currentLeftMenuVar,
} from 'Apollo/config';
import { GET_AUDIT_LOGS_FOR_METRO2, GET_FAILED_TESTS_FOR_METRO2 } from 'Apollo/Queries/Rules/Metro2Results';
import { getProfile } from 'views/Auth/AuthHelper';
import crossReportImg from 'assets/images/cross-report-black.svg';
import { PageSizeOptionEnum, ModuleEnum } from '../../../../constants';
import Metro2Results from '../components/Metro2Results';

const listItems = [
    [
        <i key={1} className="appkiticon icon-bar-chart-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem', color: '#464646' }}>Data Reconciliation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-ticket-outline" style={{ fontSize: '1.5rem', color: 'white', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem' }}>Rule Engine</span>,
    ],
    [
        <img
            key={1}
            src={crossReportImg}
            alt="Cross Report"
            style={{
                width: '30.84px', height: '25px', top: '100px',
            }}
        />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Cross Report Validation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-grow-revenue-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Trend Analytics</span>,
    ],
];

const Metro2ResultsContainer = () => {
    const {
        FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE,
    } = PageSizeOptionEnum;
    const pageSizeOptions = [FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE];
    const location = useLocation();
    const history = useHistory();
    const [batchId, setBatchId] = useState(location.state.batchId);
    const [ruleId, setRuleId] = useState(location.state.ruleId);
    const [data, setData] = useState([]);
    const [pages, setPages] = useState(null);
    const [pageSize, setPageSize] = useState(TEN);
    const [pageNumber, setPageNumber] = useState(1);

    const profile = getProfile();
    /* Apollo query to fetch audit logs */
    const auditLog = useQuery(GET_AUDIT_LOGS_FOR_METRO2, {
        variables: {
            input: {
                batchId: Number(batchId),
                ruleId: Number(ruleId),
            },
            fetchPolicy: 'no-cache',
        },
    });

    /* Apollo query to fetch failed test data */
    const [getFailedTestsData, { data: { getFailedTestResultsForMetro2 = {} } = {} }] = useLazyQuery(GET_FAILED_TESTS_FOR_METRO2, {
        variables: {
            input: {
                batchId: Number(batchId),
                ruleId: Number(ruleId),
                pageSize,
                pageNumber,
                iamUserId: profile.userId,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /** Callback for getFailedTestsData */
    const fetchData = useCallback(
        () => {
            getFailedTestsData();
        },
    );

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text" onClick={() => { history.goBack(); }} role="presentation">Metro2 Diagnostic Results</span>,
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">{`Rule #${(ruleId) || ''} Results`}</span>,
        },
    ];

    /* Method for sidebar navigation */
    const onNavigate = () => {
        // sidebar navigation
    };

    /* Column configuration for batch test summary table */
    const columns = [
        {
            Header: 'Row ID',
            accessor: 'rowId',
            minWidth: 80,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Error Message',
            accessor: 'errorMessage',
            minWidth: 200,
            resizable: true,
            sortable: true,
            Cell: row => (
                <Tooltip
                    content={row.original.errorMessage}
                    placement="top"
                    tooltipTheme="light"
                    trigger="hover"
                    style={{ width: '500px', wordWrap: 'break-word' }}
                >
                    <div
                        style={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            width: '300px',
                            display: 'block',
                            overflow: 'hidden',
                            cursor: 'pointer',
                        }}
                    >
                        {row.original.errorMessage}
                    </div>
                </Tooltip>
            ),
        },
        {
            Header: 'Error Data',
            accessor: 'errorData',
            minWidth: 200,
            resizable: true,
            sortable: true,
            Cell: row => (
                <Tooltip
                    content={row.original.errorData}
                    placement="top"
                    tooltipTheme="light"
                    trigger="hover"
                    style={{ width: '500px', wordWrap: 'break-word' }}
                >
                    <div
                        style={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            width: '300px',
                            display: 'block',
                            overflow: 'hidden',
                            cursor: 'pointer',
                        }}
                    >
                        {row.original.errorData}
                    </div>
                </Tooltip>
            ),
        },
    ];

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        currentLeftMenuVar(ModuleEnum.RULEENGINE);
    }, []);

    /* Side effect when getFailedTestResultsForMetro2 changes */
    useEffect(() => {
        if (Object.keys(getFailedTestResultsForMetro2).length > 0) {
            const totalPages = Math.ceil(getFailedTestResultsForMetro2.pagination.totalCount / pageSize);
            setData(getFailedTestResultsForMetro2.testResults);
            setPages(totalPages);
        }
    }, [getFailedTestResultsForMetro2]);

    /* Side effect when location state updates */
    useEffect(() => {
        setBatchId(location.state.batchId);
        setRuleId(location.state.ruleId);
        showReportMenuVar(true);
    }, [location]);

    return (
        <Metro2Results
            breadcrumbItems={breadcrumbItems}
            onNavigate={onNavigate}
            criteria={getFailedTestResultsForMetro2 && getFailedTestResultsForMetro2.criteria}
            columns={columns}
            fetchData={fetchData}
            data={data}
            pages={pages}
            setPageNumber={setPageNumber}
            pagesize={pageSize}
            setPageSize={setPageSize}
            auditLog={auditLog}
            refetch={getFailedTestsData}
            pageSizeOptions={pageSizeOptions}
        />
    );
};

export default Metro2ResultsContainer;
